import { db } from './firebase';
import { collection, onSnapshot, doc, setDoc, updateDoc, arrayUnion } from 'firebase/firestore';
import type { MaydayBeacon } from '../types';

type MaydayListener = (beacons: MaydayBeacon[]) => void;
const listeners: MaydayListener[] = [];

let maydaysCache: MaydayBeacon[] = [];

export function subscribeMaydays(listener: MaydayListener): () => void {
  listeners.push(listener);
  listener(getActiveMaydays());

  const colRef = collection(db, 'maydays');
  const unsub = onSnapshot(colRef, (snapshot) => {
    const items: MaydayBeacon[] = [];
    snapshot.forEach(d => items.push({ id: d.id, ...d.data() } as MaydayBeacon));
    maydaysCache = items.sort((a, b) => b.createdAt - a.createdAt);
    listeners.forEach(fn => fn(getActiveMaydays()));
  }, (err) => {
    console.warn('[Pops Culture] Firestore maydays snapshot warning:', err);
  });

  return () => {
    unsub();
    const idx = listeners.indexOf(listener);
    if (idx !== -1) listeners.splice(idx, 1);
  };
}

export function getActiveMaydays(): MaydayBeacon[] {
  const now = Date.now();
  // Filter out pings expired beyond 2 hours
  return maydaysCache.filter(m => m.expiresAt > now);
}

function notify() {
  const active = getActiveMaydays();
  listeners.forEach(fn => fn(active));
}

export async function broadcastMayday(
  user: { uid: string; name: string; avatarInitials: string },
  text: string,
  urgency: 'Right now! (Next 20 mins)' | 'Need today (within 2 hrs)' | 'This afternoon',
  area: string,
  reward?: string
): Promise<MaydayBeacon> {
  const now = Date.now();
  const beacon: MaydayBeacon = {
    id: `may_${now}`,
    authorId: user.uid,
    authorName: user.name,
    avatarInitials: user.avatarInitials,
    text: text.trim(),
    urgency,
    area: area.trim() || 'Local Neighborhood',
    reward: reward?.trim() || 'Cold six-pack of craft brew or soda & eternal gratitude',
    createdAt: now,
    expiresAt: now + 1000 * 60 * 120, // 2-hour TTL
    helpers: []
  };

  maydaysCache.unshift(beacon);
  notify();

  try {
    await setDoc(doc(db, 'maydays', beacon.id), beacon);
  } catch (e) {
    console.warn('[Pops Culture] Failed to broadcast mayday to Firestore:', e);
  }

  return beacon;
}

export async function respondToMayday(maydayId: string, helperName: string): Promise<void> {
  const target = maydaysCache.find(m => m.id === maydayId);
  if (!target) return;

  if (!target.helpers.includes(helperName)) {
    target.helpers.push(helperName);
    notify();

    try {
      const ref = doc(db, 'maydays', maydayId);
      await updateDoc(ref, {
        helpers: arrayUnion(helperName)
      });
    } catch (e) {
      console.warn('[Pops Culture] Failed to update helper in Firestore:', e);
    }
  }
}
